import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

export const PRODUCTION_TEST_CRMS = {
  '70299cd6-a817-4eeb-a333-df660b1f204e': { app: 'cinc', label: 'CINC' },
  '816d1e20-f38b-466e-96ec-46bb7d0f404c': { app: 'lofty', label: 'Lofty' },
  '6e5adb4f-c2b4-459b-8c40-f41bb801bbf2': { app: 'sierra-interactive', label: 'Sierra Interactive' },
  '048164c9-68a7-4f2c-ac30-3400e3fa47e7': { app: 'boldtrail', label: 'BoldTrail' },
} as const;

export type ProductionTestCrmOrgId = keyof typeof PRODUCTION_TEST_CRMS;

export const isProductionTestCrmOrg = (id: string | undefined): id is ProductionTestCrmOrgId =>
  typeof id === 'string' && Object.prototype.hasOwnProperty.call(PRODUCTION_TEST_CRMS, id);

export interface ProductionTestCrmStatus {
  status: 'existing_active_routing_fenced' | 'credential_bound_disabled';
  app_key: string;
  plan: string | null;
  auth_type: string | null;
  metadata: {
    read_capabilities: string[];
    profile_paths: { path: string; type: string }[];
  };
  credential_bound: true;
  read_only: true;
  provider_account_qualified: false;
  import_enabled: false;
  outreach_enabled: false;
  writes: 0;
}

export interface ProductionTestCrmFixedPerson {
  status: 'exact_fixture_read';
  credential_bound: true;
  person_id_match: true;
  synthetic_shape_match: true;
  provider_account_qualified: false;
  import_enabled: false;
  outreach_enabled: false;
  writes: 0;
}

const basePath = (orgId: ProductionTestCrmOrgId) =>
  `/api/v1/organizations/${orgId}/production-test-crm`;

export const getProductionTestCrmStatus = async (orgId: ProductionTestCrmOrgId) => {
  const response = await remiApi.get<ProductionTestCrmStatus>(`${basePath(orgId)}/status`);
  return response.data;
};

export const getProductionTestCrmFixedPerson = async (
  orgId: Exclude<ProductionTestCrmOrgId, '70299cd6-a817-4eeb-a333-df660b1f204e'>
) => {
  const response = await remiApi.get<ProductionTestCrmFixedPerson>(`${basePath(orgId)}/fixed-person`);
  return response.data;
};

export const validProductionTestCrmStatus = (
  orgId: ProductionTestCrmOrgId,
  value: ProductionTestCrmStatus
): boolean => {
  const isCinc = orgId === '70299cd6-a817-4eeb-a333-df660b1f204e';
  return value?.app_key === PRODUCTION_TEST_CRMS[orgId].app &&
    value.status === (isCinc ? 'existing_active_routing_fenced' : 'credential_bound_disabled') &&
    value.credential_bound === true && value.read_only === true &&
    value.provider_account_qualified === false && value.import_enabled === false &&
    value.outreach_enabled === false && value.writes === 0 &&
    Array.isArray(value.metadata?.read_capabilities) &&
    Array.isArray(value.metadata?.profile_paths) &&
    value.metadata.profile_paths.length === 0 &&
    value.metadata.read_capabilities.every((capability) => typeof capability === 'string') &&
    value.metadata.profile_paths.every((item) =>
      typeof item?.path === 'string' && typeof item?.type === 'string');
};

export const validProductionTestCrmFixedPerson = (value: ProductionTestCrmFixedPerson): boolean =>
  value?.status === 'exact_fixture_read' && value.credential_bound === true &&
  value.person_id_match === true && value.synthetic_shape_match === true &&
  value.provider_account_qualified === false && value.import_enabled === false &&
  value.outreach_enabled === false && value.writes === 0;
